import { createContext, useContext, useEffect, useState } from 'react';
import { useAuthentication } from '@context/businessContainer';
import { useCookies } from 'react-cookie';
import { useHouseholdSizeDBSync } from '@hooks/useHouseholdSize';
import {
  COOKIE_AUTHENTICATED,
  COOKIE_PERSISTED_CUSTOMER,
} from '@constants/cookies';
import { IPersistedAuth } from '@monofront/types';
import { IAuthState } from '@monofront/business/contexts';
import { useMemoWithPreviousValue } from '@monofront/business/hooks';

const OLD_EMAIL_TAG_START = 'oldemail_';

const PersistedAuthContext = createContext<IPersistedAuth | IAuthState>({
  customer: undefined,
  isMember: false,
  isAuthenticated: false,
  isCustomerLoading: true,
  isLoading: true,
  authUser: null,
});

export const PersistedAuthProvider = ({ children }) => {
  const [cookies, setCookie, removeCookie] = useCookies([
    COOKIE_PERSISTED_CUSTOMER,
    COOKIE_AUTHENTICATED,
  ]);
  const { customer, isCustomerLoading, isMember } = useAuthentication();

  useHouseholdSizeDBSync();

  const [isClientInitialized, setIsClientInitialized] = useState(false);
  const persistedAuthState = useMemoWithPreviousValue<IPersistedAuth>(
    (previousValue) => {
      if (!previousValue) {
        return {
          customer: cookies?.[COOKIE_PERSISTED_CUSTOMER]
            ? {
                omsTags: cookies[COOKIE_PERSISTED_CUSTOMER].omsTags ?? [],
                uid: cookies[COOKIE_PERSISTED_CUSTOMER].uid,
                address: cookies[COOKIE_PERSISTED_CUSTOMER].address,
                totalOrderPaid:
                  cookies[COOKIE_PERSISTED_CUSTOMER].totalOrderPaid,
                savedAddressCount:
                  cookies[COOKIE_PERSISTED_CUSTOMER].savedAddressCount,
              }
            : undefined,
          isAuthenticated: cookies?.[COOKIE_AUTHENTICATED] || false,
          isMember: cookies?.[COOKIE_PERSISTED_CUSTOMER]?.isMember || false,
          isCustomerLoading: true,
          isLoading: true,
          authUser: null,
        };
      }

      if (!isClientInitialized) {
        return previousValue;
      }

      if (
        !isCustomerLoading &&
        customer &&
        (previousValue.customer?.uid !== customer?.uid ||
          JSON.stringify(previousValue.customer?.omsTags) !==
            JSON.stringify(customer?.omsTags) ||
          previousValue.customer?.savedAddressCount !==
            customer?.savedAddressCount)
      ) {
        // to activate once we are ready to activate cookies
        // const uid = getCookieConsent(OneTrustCookieCategories.FUNCTIONALITY)
        //   ? customer.uid
        //   : undefined;

        const omsTags = customer.omsTags?.filter(
          (tag) => !tag.startsWith(OLD_EMAIL_TAG_START),
        );

        // to activate once we are ready to activate cookies
        // const totalOrderPaid = getCookieConsent(
        //   OneTrustCookieCategories.FUNCTIONALITY
        // )
        //   ? customer.totalOrderPaid
        //   : undefined;
        return {
          ...previousValue,
          isCustomerLoading: false,
          isMember,
          customer: customer
            ? {
                ...customer,
                omsTags, // filter the oldemail tag, as it is only used backend side
              }
            : undefined,
        };
      }

      // handle the user logout
      if (!isCustomerLoading && !customer) {
        return {
          customer: undefined,
          isMember: false,
          isAuthenticated: false,
          isCustomerLoading: true,
          isLoading: true,
          authUser: null,
        };
      }

      return previousValue;
    },
    [cookies, isClientInitialized, isCustomerLoading, customer],
  );

  useEffect(() => {
    if (!isClientInitialized) {
      setIsClientInitialized(true);
    }
  }, [isClientInitialized]);

  useEffect(() => {
    if (persistedAuthState.isCustomerLoading) {
      return;
    }

    const customer = persistedAuthState.customer;

    if (!customer) {
      removeCookie(COOKIE_PERSISTED_CUSTOMER);

      return;
    }

    // it is functionally needed for our customers that is why we don't ask for consent to persist this data (fresh and ultra fresh functionality)
    const address = customer.address
      ? {
          postalCode: customer.address.postalCode,
          country: customer.address.country,
        }
      : undefined;

    setCookie(COOKIE_PERSISTED_CUSTOMER, {
      omsTags: customer.omsTags,
      uid: customer.uid,
      address,
      totalOrderPaid: customer.totalOrderPaid,
      isMember: persistedAuthState.isMember,
      savedAddressCount: customer.savedAddressCount,
    });
  }, [persistedAuthState, removeCookie, setCookie]);

  return (
    <PersistedAuthContext.Provider value={persistedAuthState}>
      {children}
    </PersistedAuthContext.Provider>
  );
};

export const useCacheFirstAuthentication = () =>
  useContext(PersistedAuthContext);
